/**
 * 스테이킹 구독 팝업 (2026-10-02 재작성)
 *
 * 테일윈드 임의값 클래스가 퍼지로 날아가 전체화면으로 깨졌다.
 * 시세 차트 팝업과 같은 방식 — 색·여백 전부 인라인로 고정한다.
 */
import { useState } from 'react';
import { X, Lock, AlertTriangle } from 'lucide-react';

interface Product {
  id: number; name: string; apy: number; minAmount: number; lockPeriodDays: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  product: Product | null;
  eligible: number;
  onConfirm: (amount: number, pin: string) => Promise<void>;
  onGoBuy: () => void;
}

const n0 = (v: number) => Math.floor(v).toLocaleString('ko-KR');

export function StakeSubscribeModal({ open, onClose, product, eligible, onConfirm, onGoBuy }: Props) {
  const [amount, setAmount] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);

  if (!open || !product) return null;

  const months = Math.round(product.lockPeriodDays / 30);
  const short = eligible < product.minAmount;
  const v = Number(amount);
  const valid = Number.isFinite(v) && v >= product.minAmount && v <= eligible;
  const expect = valid ? Math.round(v * product.apy / 100) : 0;

  const submit = async () => {
    if (!valid || !pin || busy) return;
    setBusy(true);
    try { await onConfirm(v, pin); setAmount(''); setPin(''); }
    finally { setBusy(false); }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '14px 16px', borderRadius: 14, boxSizing: 'border-box',
    border: '2px solid #FED7AA', fontSize: 20, fontWeight: 800, textAlign: 'center',
    background: '#FFFBF7', color: '#111827', outline: 'none',
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 300, background: 'rgba(0,0,0,.6)', padding: '16px 20px 110px' }}>
      <div className="w-full flex flex-col"
        style={{ background: '#FFFFFF', color: '#111827', maxWidth: 420,
                 maxHeight: '78vh', borderRadius: 20, overflow: 'hidden' }}>

        {/* 헤더 */}
        <div className="flex items-center justify-between"
          style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
          <h3 style={{ fontSize: 18, fontWeight: 700 }}>{product.name}</h3>
          <button onClick={onClose} aria-label="닫기" className="flex items-center justify-center"
            style={{ width: 36, height: 36, borderRadius: 999, background: '#111827', flexShrink: 0 }}>
            <X className="h-5 w-5" style={{ color: '#FFFFFF' }} />
          </button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {short ? (
            <>
              <div style={{ display: 'flex', gap: 10, padding: 14, borderRadius: 14,
                            background: '#FFFBEB', border: '1px solid #FDE68A', marginBottom: 12 }}>
                <AlertTriangle style={{ width: 20, height: 20, color: '#D97706', flexShrink: 0, marginTop: 2 }} />
                <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                  <b>최소 {n0(product.minAmount)} wHANDY</b> 부터 신청할 수 있습니다.<br />
                  지금 신청 가능한 수량은 <b>{n0(eligible)} wHANDY</b> 입니다.
                </div>
              </div>
              <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.7, padding: 14,
                            borderRadius: 14, background: '#F9FAFB', marginBottom: 16 }}>
                PV 자동구매·USDT 구매로 받은 wHANDY만 신청할 수 있습니다.<br />
                에어드롭·추천보상으로 받은 물량은 제외됩니다.
              </div>
              <button onClick={() => { onClose(); onGoBuy(); }}
                style={{ width: '100%', padding: '14px 0', borderRadius: 14, fontSize: 16,
                         fontWeight: 800, background: '#FF6B00', color: '#fff', border: 0 }}>
                wHANDY 구매하러 가기 ›
              </button>
            </>
          ) : (
            <>
              {/* 수량 */}
              <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>맡길 수량</span>
                <span style={{ fontSize: 12, color: '#6B7280' }}>
                  신청 가능 {n0(eligible)} wHANDY
                </span>
              </div>
              <input
                value={amount ? Number(amount).toLocaleString() : ''}
                onChange={e => setAmount(e.target.value.replace(/[^0-9]/g, ''))}
                inputMode="numeric"
                placeholder={`최소 ${n0(product.minAmount)}`}
                style={inputStyle}
              />
              <div className="flex items-center justify-between" style={{ marginTop: 8 }}>
                {amount !== '' && !valid ? (
                  <span style={{ fontSize: 12, color: '#DC2626', fontWeight: 600 }}>
                    {v > eligible
                      ? `신청 가능 수량(${n0(eligible)})을 초과했습니다`
                      : `최소 ${n0(product.minAmount)} 부터 신청할 수 있습니다`}
                  </span>
                ) : <span />}
                <button type="button" onClick={() => setAmount(String(Math.floor(eligible)))}
                  style={{ fontSize: 12, fontWeight: 800, color: '#FF6B00', background: '#FFF4EC',
                           border: 0, borderRadius: 8, padding: '6px 12px' }}>
                  전액 입력
                </button>
              </div>

              {/* 수익 요약 */}
              <div style={{ background: '#F9FAFB', borderRadius: 14, padding: 14, marginTop: 14 }}>
                <div className="flex items-center justify-between" style={{ fontSize: 14 }}>
                  <span style={{ color: '#6B7280' }}>수익률</span>
                  <b>{product.apy}% · {months}개월</b>
                </div>
                <div className="flex items-center justify-between" style={{ fontSize: 14, marginTop: 8 }}>
                  <span style={{ color: '#6B7280' }}>예상 수익</span>
                  <b style={{ color: '#16A34A' }}>{valid ? `+${expect.toLocaleString()} wHANDY` : '-'}</b>
                </div>
              </div>

              {/* 잠금 안내 */}
              <div style={{ display: 'flex', gap: 10, padding: 14, borderRadius: 14, marginTop: 12,
                            background: '#FFFBEB', border: '1px solid #FDE68A' }}>
                <Lock style={{ width: 18, height: 18, color: '#D97706', flexShrink: 0, marginTop: 2 }} />
                <p style={{ fontSize: 13, lineHeight: 1.6, margin: 0 }}>
                  맡긴 수량은 <b>{months}개월간 잠깁니다.</b> 만기에 원금과 수익을 한 번에 받습니다.
                </p>
              </div>

              {/* 비밀번호 */}
              <p style={{ fontSize: 14, fontWeight: 700, margin: '14px 0 8px' }}>비밀번호</p>
              <input
                value={pin}
                onChange={e => setPin(e.target.value)}
                type="password" inputMode="numeric" placeholder="비밀번호 입력"
                style={{ ...inputStyle, border: '1px solid #E5E7EB', background: '#F9FAFB',
                         fontSize: 18, letterSpacing: 4 }}
              />
            </>
          )}
        </div>

        {/* 푸터 */}
        {!short && (
          <div style={{ padding: '12px 20px 20px', borderTop: '1px solid #f3f4f6' }}>
            <button onClick={submit} disabled={!valid || !pin || busy}
              style={{ width: '100%', padding: '15px 0', borderRadius: 14, fontSize: 17,
                       fontWeight: 800, background: '#FF6B00', color: '#fff', border: 0,
                       opacity: !valid || !pin || busy ? .45 : 1,
                       boxShadow: '0 6px 16px rgba(255,107,0,.3)' }}>
              {busy ? '처리 중…' : '구독하기'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
